/**
 * Message actions (DESIGN.md §18): the ONE definition behind the reading-pane toolbar, the "…"
 * menu, the list's context menu, the bulk bar, the command palette and the Outlook shortcuts.
 * Handlers come from the mail page; capabilities the server lacks become "Planned" entries.
 */
import { defineActions, type ActionContext, type ActionRegistry, type ShortcutAction } from '@platform/ui';
import {
  CodeIcon,
  CopyIcon,
  DownloadIcon,
  FlagIcon,
  ForwardIcon,
  FolderInputIcon,
  InfoIcon,
  MailIcon,
  MailOpenIcon,
  MailPlusIcon,
  PrinterIcon,
  ReplyAllIcon,
  ReplyIcon,
  ShieldAlertIcon,
  TagIcon,
  Trash2Icon,
  type LucideIcon,
} from 'lucide-react';
import type { MessageSummary } from '../api';
import type { ComposeMode } from '../mail-context';

/** Read-only message tools (T10 "Message actions"): dialogs, download, print. */
export type MessageTool = 'details' | 'viewSource' | 'downloadEml' | 'print' | 'protectedMail';

export interface MessageActionHandlers {
  open(message: MessageSummary): void;
  respond(mode: Exclude<ComposeMode, 'new' | 'draft'>, message: MessageSummary): void;
  setRead(messages: readonly MessageSummary[], read: boolean): void;
  setFlag(messages: readonly MessageSummary[], flagged: boolean): void;
  /** Opens the folder picker. */
  pickFolder(mode: 'move' | 'copy', messages: readonly MessageSummary[]): void;
  remove(messages: readonly MessageSummary[]): void | Promise<void>;
  tool(tool: MessageTool, message: MessageSummary): void;
}

export interface MessageActionOptions {
  /** Server capabilities (`WebmailApi` optional methods). */
  canFlag: boolean;
  canMove: boolean;
  canCopy: boolean;
  /** The open folder is Deleted Items: delete is permanent and asks first. */
  inDeletedItems: boolean;
}

type Context = ActionContext<MessageSummary>;

const RESPOND: readonly [Exclude<ComposeMode, 'new' | 'draft'>, 'mail.reply' | 'mail.replyAll' | 'mail.forward', LucideIcon][] = [
  ['reply', 'mail.reply', ReplyIcon],
  ['replyAll', 'mail.replyAll', ReplyAllIcon],
  ['forward', 'mail.forward', ForwardIcon],
];

/** Outlook "…" menu order: details, source, download, print. */
const TOOLS: readonly [MessageTool, 'webmail.reader.details' | 'webmail.reader.viewSource' | 'webmail.reader.downloadEml' | 'webmail.reader.print' | 'mailCrypto.open', LucideIcon, ShortcutAction | undefined][] = [
  ['details', 'webmail.reader.details', InfoIcon, 'mail.details'],
  ['viewSource', 'webmail.reader.viewSource', CodeIcon, 'mail.viewSource'],
  ['protectedMail', 'mailCrypto.open', ShieldAlertIcon, undefined],
  ['downloadEml', 'webmail.reader.downloadEml', DownloadIcon, undefined],
  ['print', 'webmail.reader.print', PrinterIcon, 'mail.print'],
];

const anyUnread = ({ targets }: Context) => targets.some((message) => message.unread);
const anyRead = ({ targets }: Context) => targets.some((message) => !message.unread);
const allFlagged = ({ targets }: Context) => targets.every((message) => message.flagged);

export function createMessageActions(handlers: MessageActionHandlers, options: MessageActionOptions): ActionRegistry<MessageSummary> {
  const one = ({ targets }: Context) => targets[0]!;
  return defineActions<MessageSummary>([
    {
      id: 'mail.open',
      label: 'webmail.actions.open',
      icon: MailOpenIcon,
      group: 'open',
      defaultGesture: true,
      surfaces: ['context'],
      run: (context) => handlers.open(one(context)),
    },
    ...RESPOND.map(([mode, id, icon]) => ({
      id,
      label: `webmail.reader.${mode}` as const,
      icon,
      shortcut: id,
      group: 'respond',
      surfaces: ['toolbar', 'context', 'palette'] as const,
      run: (context: Context) => handlers.respond(mode, one(context)),
    })),
    {
      id: 'mail.forwardAsAttachment',
      label: 'webmail.reader.forwardAsAttachment',
      icon: MailPlusIcon,
      shortcut: 'mail.forwardAsAttachment',
      group: 'respond',
      run: (context) => handlers.respond('forwardAsAttachment', one(context)),
    },
    {
      id: 'mail.markRead',
      label: 'webmail.reader.markRead',
      icon: MailOpenIcon,
      shortcut: 'mail.markRead',
      group: 'state',
      arity: 'many',
      surfaces: ['toolbar', 'context', 'palette'],
      visible: anyUnread,
      run: ({ targets }) => handlers.setRead(targets, true),
    },
    {
      id: 'mail.markUnread',
      label: 'webmail.reader.markUnread',
      icon: MailIcon,
      shortcut: 'mail.markUnread',
      group: 'state',
      arity: 'many',
      surfaces: ['toolbar', 'context', 'palette'],
      visible: anyRead,
      run: ({ targets }) => handlers.setRead(targets, false),
    },
    {
      id: 'mail.flag',
      label: 'webmail.actions.flag',
      labelFor: (context) => (allFlagged(context) ? 'webmail.actions.clearFlag' : 'webmail.actions.flag'),
      icon: FlagIcon,
      shortcut: 'mail.flag',
      group: 'state',
      arity: 'many',
      planned: !options.canFlag,
      run: (context) => handlers.setFlag(context.targets, !allFlagged(context)),
    },
    {
      id: 'mail.categorize',
      label: 'webmail.actions.categorize',
      icon: TagIcon,
      group: 'state',
      arity: 'many',
      // Categories (Outlook master category list) are not modelled yet.
      planned: true,
      run: () => undefined,
    },
    {
      id: 'mail.move',
      label: 'webmail.actions.move',
      icon: FolderInputIcon,
      shortcut: 'mail.move',
      group: 'organize',
      arity: 'many',
      planned: !options.canMove,
      run: ({ targets }) => handlers.pickFolder('move', targets),
    },
    {
      id: 'mail.copy',
      label: 'webmail.actions.copy',
      icon: CopyIcon,
      group: 'organize',
      arity: 'many',
      planned: !options.canCopy,
      run: ({ targets }) => handlers.pickFolder('copy', targets),
    },
    {
      id: 'mail.junk',
      label: 'webmail.actions.junk',
      icon: ShieldAlertIcon,
      group: 'organize',
      arity: 'many',
      // Feeds the planned personal block/allow lists (scanner, mail-scanner.md §12).
      planned: true,
      run: () => undefined,
    },
    {
      id: 'mail.delete',
      label: 'webmail.reader.delete',
      labelFor: () => (options.inDeletedItems ? 'webmail.actions.deletePermanently' : 'webmail.reader.delete'),
      icon: Trash2Icon,
      shortcut: 'mail.delete',
      group: 'delete',
      danger: true,
      arity: 'many',
      surfaces: ['toolbar', 'context', 'palette'],
      // Moving to Deleted Items is reversible (no question); deleting from there is not (T8).
      confirm: ({ targets }) =>
        options.inDeletedItems
          ? {
              title: { key: 'webmail.actions.deletePermanentlyTitle', params: { count: targets.length } },
              description: 'webmail.actions.deletePermanentlyDescription',
              confirm: 'webmail.actions.deletePermanently',
            }
          : undefined,
      run: ({ targets }) => handlers.remove(targets),
    },
    ...TOOLS.map(([tool, label, icon, shortcut]) => ({
      id: `mail.${tool}`,
      label,
      icon,
      shortcut,
      group: 'view',
      run: (context: Context) => handlers.tool(tool, one(context)),
    })),
  ]);
}

